<template>
  <div>
    <div class="d-flex align-center" style="min-height: 40px">
      <b>{{ $t('PROJECT_SHEET.WAYPOINTS') }}</b>
      <v-spacer></v-spacer>
      <v-btn-icon v-if="userIsProjectAdmin" size="small" :title="$t('PROJECT_SHEET.WAYPOINT.ADD')" class="export-hidden" @click="addStep">
        <v-icon size="large">far fa-plus</v-icon>
      </v-btn-icon>
      <dashboards-filters-export v-if="currentStep" :exporting="exporting" pdf csv @pdf-export="pdfExport" @csv-export="csvExport"></dashboards-filters-export>
    </div>

    <v-card id="projectsheet-waypoint" class="pa-6">
      <div v-if="currentStep">
        <div v-if="stepEditing">
          <div class="d-flex align-center">
            <button type="button" :class="{ invisible: ! hasPrevStep }" class="export-hidden" @click="toNextStep(-1)">
              <i class="fa fa-caret-left"></i>
            </button>
            <div class="mx-4 pb-1 flex-grow-1" style="max-width: 200px;">
              <date-field v-model="currentStepDate" density="compact" no-null></date-field>
            </div>
            <button type="button" :class="{ invisible: ! hasNextStep }" class="export-hidden" @click="toNextStep()">
              <i class="fa fa-caret-right"></i>
            </button>
            <v-spacer></v-spacer>
            <template v-if="userIsProjectAdmin">
              <v-btn-icon color="errorred" size="small" @click="deleteStep">
                <v-icon>far fa-trash-alt</v-icon>
              </v-btn-icon>
            </template>
          </div>

          <div class="mt-4">
            <div v-for="moodicon in ['sun', 'suncloud', 'cloud', 'storm']" :key="moodicon" :style="currentStep.mood == moodicon ? { border: '3px solid green', padding: '6px' } : null"
                 class="pointer vcenter moodicon-wrapper" @click="currentStep.mood == moodicon ? currentStep.mood = null : currentStep.mood = moodicon">
              <div :class="`mood-icon-${moodicon}`" :title="$t(`MOOD.${$filters.uppercase(moodicon)}`)"></div>
            </div>
          </div>

          <div class="mt-4">
            <quill v-model:content="currentStep.waypoint" :default-content="defaultStepWaypoint" min-height="150px" max-height="300px"></quill>
          </div>

          <div class="text-center">
            <v-btn rounded variant="outlined" class="mx-2" @click="resetSteps">{{ $t('GLOBAL.CANCEL') }}</v-btn>
            <v-btn color="primary" rounded class="mx-2" @click="saveSteps">{{ $t('GLOBAL.SAVE') }}</v-btn>
          </div>
        </div>

        <div v-else>
          <div class="d-flex align-center mb-2">
            <template v-if="displayMode == 'text'">
              <button type="button" :class="{ invisible: ! hasPrevStep }" class="mr-2 export-hidden" @click="toNextStep(-1)">
                <i class="fa fa-caret-left"></i>
              </button>
              <div class="flex-center-center" style="min-width: 160px">
                <span class="font-weight-bold">{{ $filters.moment(currentStep.date, 'mediumDate') }}</span>
                <v-btn-icon v-if="userIsProjectAdmin" size="x-small" class="mx-1 export-hidden" @click="stepEditing = ! stepEditing">
                  <v-icon>far fa-pencil-alt</v-icon>
                </v-btn-icon>
              </div>
              <button type="button" :class="{ invisible: ! hasNextStep }" class="export-hidden" @click="toNextStep()">
                <i class="fa fa-caret-right"></i>
              </button>
            </template>

            <v-spacer></v-spacer>
            <btn-toggle-filter v-model="displayMode" mandatory="force" variant="outlined" density="compact" class="export-hidden">
              <v-btn value="graphic" class="px-3">
                <v-icon style="width: 24px">far fa-arrow-trend-up</v-icon>
              </v-btn>
              <v-btn value="text" class="px-3">
                <v-icon style="width: 24px">far fa-align-justify</v-icon>
              </v-btn>
            </btn-toggle-filter>
          </div>

          <div v-if="displayMode == 'text'" style="min-height: 210px">
            <div class="d-flex gap-4">
              <div v-if="currentStep.mood" style="width: 80px; height: 80px; flex: 0 0 80px;">
                <div :class="`mood-icon-${currentStep.mood}`" :title="$t(`MOOD.${$filters.uppercase(currentStep.mood)}`)"></div>
              </div>
              <div class="quill-html" v-html="currentStep.waypoint"></div>
            </div>

            <div v-if="filteredStepsExtraFields" style="display: flex; flex-wrap: wrap; margin: 8px -8px 0">
              <div v-for="field in filteredStepsExtraFields" :key="field.name"
                   :style="{ width: `${(field.width || 1) * 100}%` }" class="mb-1 px-2" style="flex-grow: 1;">
                <i v-if="field.icon" :class="field.icon" class="mr-2"></i>
                <b>{{ field.title }}</b>
                <div class="quill-html" v-html="currentStep[field.name]"></div>
              </div>
            </div>
          </div>

          <div v-else class="mt-4">
            <project-sheet-waypoints-chart v-model:current-step-index="currentStepIndex" v-model:display-mode="displayMode" :steps="steps" @add-step="addStep"></project-sheet-waypoints-chart>
          </div>
        </div>
      </div>
      <div v-else-if="userIsProjectAdmin" class="position-relative overflow-hidden export-hidden">
        <project-sheet-waypoints-chart :steps="[{ mood: 'suncloud' }, { mood: 'cloud' }, { mood: 'sun' }]" style="opacity: .4; pointer-events: none; filter: grayscale(1); margin-bottom: -70px">
        </project-sheet-waypoints-chart>
        <div class="flex-center-center" style="position: absolute; inset: 0">
          <v-btn size="small" rounded variant="outlined" class="bg-white" @click="addStep">
            <v-icon class="mr-2">far fa-plus</v-icon>{{ $t('PROJECT_SHEET.WAYPOINT.ADD') }}
          </v-btn>
        </div>
      </div>
      <div v-else class="text-center text-medium-emphasis">
        {{ $t('PROJECT_SHEET.WAYPOINT.NO_WAYPOINT') }}
      </div>
    </v-card>
  </div>
</template>

<script>
  import clientConfig from '@/client_customs/config';
  import Quill from '@/components/Reusables/Quill.vue';
  import ProjectSheetWaypointsChart from '@/components/ProjectSheet/ProjectSheetWaypointsChart.vue';
  import DashboardsFiltersExport from '@/components/Dashboards/DashboardsFilters/DashboardsFiltersExport.vue';
  import useUsersAccessRightStore from '@/stores/users/accessRight';

  function deepcopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  }

  export default {
    components: {
      Quill,
      ProjectSheetWaypointsChart,
      DashboardsFiltersExport,
    },
    props: {
      planning: { type: Object, required: true },
      closeEditing: { type: Boolean, default: false },
    },
    emits: ['update'],
    data() {
      const projectsheet = this.planning.projectsheet || {};
      const steps = deepcopy(projectsheet.steps || []);
      const userIsProjectAdmin = this.planning.meta.access_right == 'admin';

      return {
        steps,
        currentStepIndex: steps.length - 1,
        displayMode: 'graphic',
        stepEditing: false,
        defaultStepWaypoint: clientConfig.projectsheet?.defaultStepWaypoint || '',
        exporting: { inprogress: false, success: false, error: false },
        userIsProjectAdmin,
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      currentStep() { return this.steps[this.currentStepIndex]; },
      currentStepDate: {
        get() { return this.currentStep?.date; },
        set(newVal) {
          if (! newVal) return;
          const step = this.currentStep;
          step.date = newVal.format();
          this.steps.sort((a, b) => (moment(a.date).isBefore(b.date) ? -1 : 1));
          this.currentStepIndex = this.steps.indexOf(step);
        },
      },
      hasPrevStep() {
        return this.currentStepIndex >= 1;
      },
      hasNextStep() {
        return this.currentStepIndex < this.steps.length - 1;
      },
      stepsExtraFields() {
        // [{"title":"Points d'attention","name":"issues","width":0.5},{"title":"Points de blocage à lever","name":"actions","width":0.5}]
        return this.usersAccessRightStore.config.projectsheet_steps_extrafields;
      },
      filteredStepsExtraFields() {
        return this.stepsExtraFields?.filter(field => this.hasContent(this.currentStep[field.name]));
      },
      safeProjectName() {
        return this.planning.getTitle().replace(/<(?:.|\n)*?>/gm, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/\//g, ' ');
      },
    },
    watch: {
      closeEditing(newVal) {
        if (newVal) this.stepEditing = false;
      },
    },
    created() {
      this.steps.forEach((step) => { step.id = step.id || window.uuid(); }); // init id for old versions
    },
    methods: {
      toNextStep(amount = 1) {
        this.currentStepIndex += amount;
      },
      addStep() {
        const newStep = { id: window.uuid(), waypoint: "", date: moment().format(), mood: null, updated_at: moment().format() };
        this.steps.push(newStep);
        this.steps.sort((a, b) => (moment(a.date).isBefore(b.date) ? -1 : 1));
        this.currentStepIndex = this.steps.indexOf(newStep);
        this.stepEditing = true;
      },
      deleteStep() {
        if (this.currentStep) this.steps.splice(this.currentStepIndex, 1);
        this.currentStepIndex = Math.min(this.currentStepIndex, this.steps.length - 1);
        if (! this.steps.length) this.saveSteps();
      },
      resetSteps() {
        const projectsheet = this.planning.projectsheet || {};
        this.steps = deepcopy(projectsheet.steps || []);
        this.currentStepIndex = Math.min(this.currentStepIndex, this.steps.length - 1);
        this.stepEditing = false;
      },
      saveSteps() {
        const projectsheet = this.planning.projectsheet || {};
        this.steps.forEach((step) => {
          if (! step.id) return;
          const originalStep = (projectsheet.steps || []).find(item => item.id == step.id);
          if (originalStep && ! angular.equals(originalStep, step)) step.updated_at = moment().format();
        });

        this.$emit('update', {
          steps: deepcopy(this.steps),
        });
        this.stepEditing = false;
      },
      hasContent(html) {
        return !! window.html2text(html || '').trim();
      },
      csvExport() {
        const filename = `${this.planning.getTitle().replace('.', '')} - ${this.$t('PROJECT_SHEET.WAYPOINTS')}.csv`;
        const separator = ';';
        let csvContent = '';

        function excelEncode(val) {
          return `"${val ? val.replace(/"/g, '""') : ''}"`;
        }

        const headers = [this.$t('PROJECT_SHEET.WAYPOINT.DATE'), this.$t('PROJECT_SHEET.WAYPOINT.MOOD'), this.$t('PROJECT_SHEET.WAYPOINT.TITLE')];
        csvContent += headers.map(h => excelEncode(h)).join(separator);
        csvContent += '\n';
        this.steps.forEach((step) => {
          const waypoint = (step.waypoint || '').replace(/<\/p>/gi, '</p>\n').replace(/<\/li>/gi, '</li>\n'); // add line breaks after paragraphs and list items
          const row = [
            excelEncode(step.date ? moment(step.date).format('L') : ''),
            excelEncode(step.mood ? this.$t(`MOOD.${step.mood.toUpperCase()}`) : ''),
            excelEncode(window.html2text(waypoint).trim('\n')),
          ];
          csvContent += `${row.join(separator)}\n`;
        });

        const encodedUri = encodeURI(`data:text/csv;charset=utf-8,\uFEFF${csvContent}`).replace(/#/g, '%23');
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', filename);
        document.body.appendChild(link); // Required for FF
        link.click();
        document.body.removeChild(link);
      },
      async pdfExport() {
        if (! this.steps.length) return;

        _.extend(this.exporting, { inprogress: true, success: false, error: false });
        const initialCurrentStepIndex = this.currentStepIndex;
        const initialDisplayMode = this.displayMode;
        this.displayMode = 'text';
        const $html = document.querySelector('html').cloneNode(true);
        $html.querySelector('body').innerHTML = `<div class="v-application mx-4"><div class="w-100 pa-4"><b>${this.planning.getTitle()} - ${this.$t('PROJECT_SHEET.WAYPOINTS')}</b></div></div>`;
        $html.querySelector('body').classList.add('bg-background');

        for (let i = this.steps.length - 1; i >= 0; i--) {
          this.currentStepIndex = i;
          // eslint-disable-next-line no-await-in-loop
          await this.$nextTick(); // wait for DOM update
          const queryEl = document.querySelector('#projectsheet-waypoint');
          if (queryEl) $html.querySelector('body .v-application > .w-100').innerHTML += `<div class="mt-4">${queryEl.outerHTML}</div>`;
        }

        this.currentStepIndex = initialCurrentStepIndex;
        this.displayMode = initialDisplayMode;
        $html.querySelectorAll('.export-hidden').forEach((el) => { el.parentNode.removeChild(el); });
        $html.querySelectorAll('.v-card').forEach(el => el.style.setProperty('break-inside', 'avoid'));

        window.apiSrv.call('pdf', 'store', { html: $html.innerHTML, orientation: 'portrait', footer: `${this.$t('PROJECT_SHEET.WAYPOINTS')} - ${this.planning.getTitle()}` }).then((response) => {
          if (response?.data?.pdfurl) {
            this.exporting.inprogress = false;
            this.exporting.success = true;
            setTimeout(() => { this.exporting.success = false; }, 3000);
            window.open(`${response.data.pdfurl}/${this.safeProjectName} - ${this.$t('PROJECT_SHEET.WAYPOINTS')}.pdf`, '_blank');
          }
        }).catch((message) => {
          this.exporting.inprogress = false;
          this.exporting.error = message || 'Error : not exported';
        });
      },
    },
  };
</script>
